import { useEffect, useState } from "react";
import { keepPreviousData } from "@tanstack/react-query";
import type { FigurinhaDaBiblioteca } from "@foto/shared";
import { trpc } from "../../lib/trpc.js";
import { Pagina } from "../../components/Pagina.js";
import { Botao, Entrada, Falha, Girando, Segmentos, Vazio } from "../../components/ui.js";
import { useAuth } from "../auth/useAuth.js";
import { Aviso } from "./Aviso.js";
import { DetalheDaFigurinha } from "./DetalheDaFigurinha.js";
import { EnviarFigurinha } from "./EnviarFigurinha.js";
import { GradeDeFigurinhas } from "./GradeDeFigurinhas.js";
import { VisorDaFigurinha } from "./VisorDaFigurinha.js";
import { LIMITE_DA_LISTA, contagemDeImagens } from "./apresentacao.js";
import { mensagemDeErro } from "./erros.js";
import { ABAS_DE_TIPO, tipoDoFiltro, type FiltroDeTipo } from "./tipos.js";
import { useAtraso } from "./useAtraso.js";

/** Quanto a busca espera o dedo parar antes de ir ao servidor. */
const ATRASO_DA_BUSCA_MS = 300;

/**
 * A biblioteca de figurinhas, logos e artes de letras: as imagens com fundo transparente que se colocam
 * sobre as fotos nas folhas de impressão.
 *
 * Cada casa vê as suas e as públicas; o administrador vê todas, com o nome do dono. Quem pode mudar o quê
 * é decidido pelo servidor (`podeEditar` em cada imagem) — a tela só esconde o que ele recusaria.
 */
export function FigurinhasPage() {
  const { ehAdmin } = useAuth();
  const utils = trpc.useUtils();
  const [filtro, setFiltro] = useState<FiltroDeTipo>("todas");
  const [busca, setBusca] = useState("");
  const [enviando, setEnviando] = useState(false);
  const [aberta, setAberta] = useState<FigurinhaDaBiblioteca | null>(null);
  const [ampliada, setAmpliada] = useState<FigurinhaDaBiblioteca | null>(null);
  const [aviso, setAviso] = useState<string | null>(null);

  const termo = useAtraso(busca.trim(), ATRASO_DA_BUSCA_MS);
  const tipo = tipoDoFiltro(filtro);
  const filtrando = tipo !== undefined || termo !== "" || busca.trim() !== "";

  const lista = trpc.figurinhas.list.useQuery(
    { ...(tipo && { tipo }), ...(termo && { busca: termo }), limite: LIMITE_DA_LISTA },
    {
      // Digitar na busca não pode esvaziar a grade a cada letra: a anterior fica, esmaecida, até a nova chegar.
      placeholderData: keepPreviousData,
      // As URLs das miniaturas são assinadas e vencem em uma hora: quem volta à aba depois disso refaz a
      // consulta, em vez de ver as imagens quebradas.
      refetchOnWindowFocus: true,
    },
  );

  // O aviso ("foi apagada") some sozinho: é para ser lido uma vez, não para ficar na tela.
  useEffect(() => {
    if (!aviso) return;
    const t = setTimeout(() => setAviso(null), 8000);
    return () => clearTimeout(t);
  }, [aviso]);

  const itens = lista.data ?? [];

  function limparFiltros() {
    setFiltro("todas");
    setBusca("");
  }

  function aoEnviada(item: FigurinhaDaBiblioteca) {
    setEnviando(false);
    // A imagem nova tem de aparecer na lista que está por trás do detalhe: uma aba de outro tipo, ou uma
    // busca que não a acha, a esconderia, e a pessoa fecharia o detalhe sem ver o que acabou de enviar.
    if (filtro !== "todas" && filtro !== item.tipo) setFiltro("todas");
    setBusca("");
    // Todas as consultas de lista, e não só a que está na tela: a que vai ficar na tela (outra aba, sem
    // busca) é outra consulta, que o cache ainda considera fresca e mostraria sem a imagem nova.
    void utils.figurinhas.list.invalidate();
    setAberta(item);
  }

  // Só um botão âmbar por tela: na biblioteca vazia o convite do meio é a ação principal, e o do alto
  // vira discreto.
  const bibliotecaVazia = !lista.isPending && itens.length === 0 && !filtrando && !lista.isError;
  const botaoEnviar = (principal: boolean) => (
    <Botao tom={principal ? "brasa" : "quieto"} onClick={() => setEnviando(true)}>
      Enviar imagem
    </Botao>
  );

  return (
    <Pagina
      titulo="Figurinhas, logos e letras"
      descricao="Imagens com fundo transparente para colocar sobre as fotos nas folhas de impressão."
      acao={botaoEnviar(!bibliotecaVazia)}
    >
      <div className="flex flex-col gap-4">
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <Segmentos
            rotulo="Filtrar por tipo"
            valor={filtro}
            opcoes={ABAS_DE_TIPO}
            aoMudar={setFiltro}
            className="self-start sm:self-auto"
          />
          <Entrada
            type="search"
            value={busca}
            maxLength={100}
            enterKeyHint="search"
            autoComplete="off"
            placeholder="Buscar por nome ou descrição"
            aria-label="Buscar por nome ou descrição"
            onChange={(e) => setBusca(e.target.value)}
            className="sm:max-w-sm sm:flex-1"
          />
        </div>

        {aviso && (
          // Preso sob a barra do app: quem apagou uma imagem estava no meio da grade, e um aviso lá em
          // cima, fora da tela, seria a confirmação que ninguém chega a ler.
          <div className="sticky top-[calc(var(--altura-da-barra)+0.5rem)] z-10 rounded-peca bg-fundo">
            <Aviso tom="informacao" className="flex items-start justify-between gap-3">
              <p role="status" className="min-w-0 flex-1 wrap-anywhere">
                {aviso}
              </p>
              <button
                type="button"
                onClick={() => setAviso(null)}
                className="-my-2 -mr-2 inline-flex min-h-11 shrink-0 items-center px-2 text-texto-suave underline underline-offset-2 hover:text-texto"
              >
                Dispensar
              </button>
            </Aviso>
          </div>
        )}

        {lista.isPending ? (
          <div
            role="status"
            aria-label="Carregando as imagens"
            className="flex justify-center py-16"
          >
            <Girando className="size-6 text-texto-fraco" />
          </div>
        ) : lista.isError && !lista.data ? (
          <Falha
            mensagem={`Não foi possível carregar a biblioteca. ${mensagemDeErro(lista.error)}`}
            tentarNovamente={() => void lista.refetch()}
          />
        ) : itens.length === 0 ? (
          filtrando ? (
            <Vazio
              titulo="Nada encontrado"
              descricao="Nenhuma imagem combina com o que você procura. Tente outra palavra ou veja todas."
              acao={
                <Botao tom="quieto" onClick={limparFiltros}>
                  Limpar filtros
                </Botao>
              }
            />
          ) : (
            <Vazio
              titulo="A biblioteca está vazia"
              descricao="Envie figurinhas, logos ou artes de letras em PNG ou WebP com fundo transparente. Elas ficam prontas para usar nas folhas de impressão."
              acao={botaoEnviar(true)}
            />
          )
        ) : (
          <>
            <p role="status" className="text-[0.8125rem] text-texto-suave">
              {contagemDeImagens(itens.length)}
            </p>
            <GradeDeFigurinhas
              itens={itens}
              ehAdmin={ehAdmin}
              desatualizada={lista.isPlaceholderData}
              aoAbrir={setAberta}
              aoAmpliar={setAmpliada}
            />
          </>
        )}
      </div>

      {enviando && <EnviarFigurinha aoFechar={() => setEnviando(false)} aoEnviada={aoEnviada} />}

      {aberta && (
        <DetalheDaFigurinha
          // Outra imagem é outro formulário: sem a chave, o rascunho de uma passaria para a seguinte.
          key={aberta.id}
          inicial={aberta}
          aoFechar={() => setAberta(null)}
          aoApagada={(nome) => {
            setAberta(null);
            setAviso(`«${nome}» foi apagada, com a descrição e os arquivos.`);
          }}
        />
      )}

      {ampliada && <VisorDaFigurinha item={ampliada} aoFechar={() => setAmpliada(null)} />}
    </Pagina>
  );
}
